import { useState } from "react";
import { Mui, MuiIcons } from "@osu/react-ui";
import DemographicItem from "./demographicItem";
import { useApplicantDetailsState } from "../hooks/useApplicantDetailsState";
import { humanizeEnum } from "../../util/functions";
import { APPLICATION_STATUS_LABELS, REVIEW_TYPE_LABELS, STUDENT_REQUEST_TYPE_LABELS, DECISION_LABELS } from "../../util/constants";

const accordionStyle = {
    boxShadow: "none",
    border: "none",
    borderBottom: "1px solid #ddd",
    '&:before': { display: 'none' }
};

const accordionSpecialStyle = {
    paddingTop: 1,
    boxShadow: "none",
    border: "none",
    borderBottom: "1px solid #ddd",
    '&:before': { display: 'none' }
}; 

const summaryStyle = {
    px: 0,
    minHeight: "40px",
    '& .MuiAccordionSummary-content': { margin: 0 },
    '& .MuiAccordionSummary-expandIconWrapper': { marginRight: 0 }
};

export default function AdministratorDetails() {
    const { data } = useApplicantDetailsState();
    const info = data?.details.administratorDetails ?? {};
    
    const [expanded, setExpanded] = useState({ 
    msp: false,
    commentCodes: false,
    studentGroups: false,
    studentRequest: false,
    decisionComms: false,
    meritScholarship: false,
    serviceIndicators: false,
    reviewTypeDetails: false,
    eminenceDetails: false,
    presidentsScholarship: false,
    publishDetails: false,
});
    
    const isScholars = info?.programId === "scholars"; // this is temporary
    const handleExpand = key => (event, isExp) => {
        setExpanded(prev => ({ ...prev, [key]: isExp }));
    };

    return (
        <Mui.Grid container spacing={2}>
            <Mui.Grid item xs={4}>
                <Mui.List>
                    <DemographicItem label="Review Status" value={humanizeEnum(info.reviewStatus, APPLICATION_STATUS_LABELS)} />
                    <DemographicItem label="Admissions Status" value={info.admissionStatus} />
                    <DemographicItem label="Admissions Status Date" value={info.admissionStatusDate} />
                    <DemographicItem label="Admit Date" value={info.admitDate} />
                    <DemographicItem label="Siblings" value={info.siblings} />
                    {isScholars && <DemographicItem label="Gender" value={info.gender} />}
                    <DemographicItem label="Gap Year" value={info.isGapYear} />

                    {/* MSP Accordion */}
                    <Mui.Accordion sx={accordionStyle} expanded={expanded.msp} onChange={handleExpand("msp")}>
                        <Mui.AccordionSummary expandIcon={<MuiIcons.ExpandMore />} sx={summaryStyle}>
                            <Mui.Typography sx={{ fontWeight: "bold", fontSize: "0.85rem" }}>MSP Details</Mui.Typography>
                        </Mui.AccordionSummary>
                        <Mui.AccordionDetails>
                            <Mui.List sx={{ width: "100%" }}>
                                <DemographicItem label="Applicant" value={info.msp} />
                                <DemographicItem label="Awardee" value={info.mspAwardee} />
                                <DemographicItem label="Type" value={info.mspType} />
                            </Mui.List>
                        </Mui.AccordionDetails>
                    </Mui.Accordion>

                    {/* Comment Codes Accordion sorting by most recent date first */}
                    <Mui.Accordion sx={accordionStyle} expanded={expanded.commentCodes} onChange={handleExpand("commentCodes")}>
                    <Mui.AccordionSummary expandIcon={<MuiIcons.ExpandMore />} sx={summaryStyle}>
                        <Mui.Typography sx={{ fontWeight: "bold", fontSize: "0.85rem" }}>Comment Codes</Mui.Typography>
                    </Mui.AccordionSummary>
                    <Mui.AccordionDetails>
                        <Mui.List sx={{ width: "100%" }}>
                            {(Array.isArray(info.commentCodes) && info.commentCodes.length > 0
                                ? [...info.commentCodes].sort((a, b) => new Date(b.commentDate ?? 0) - new Date(a.commentDate ?? 0))
                                : [{ category: "-", commentDate: "-" }]
                            ).map((code, idx) => (
                                <Mui.ListItem key={idx} sx={{ display: "flex", justifyContent: "space-between", px: 0 }} tabindex="0">
                                    <Mui.Typography variant="body2" sx={{ fontWeight: "bold" }}>
                                        Category: {code.category}
                                    </Mui.Typography>
                                    <Mui.Typography variant="body2" sx={{ fontWeight: "bold" }}>
                                        Date: {code.commentDate}
                                    </Mui.Typography>
                                </Mui.ListItem>
                            ))}
                        </Mui.List>
                    </Mui.AccordionDetails>
                    </Mui.Accordion>


                    {/* Student Groups Accordion */}
                    <Mui.Accordion sx={accordionStyle} expanded={expanded.studentGroups} onChange={handleExpand("studentGroups")}>
                    <Mui.AccordionSummary expandIcon={<MuiIcons.ExpandMore />} sx={summaryStyle}>
                        <Mui.Typography sx={{ fontWeight: "bold", fontSize: "0.85rem" }}>Student Groups</Mui.Typography>
                    </Mui.AccordionSummary>
                    <Mui.AccordionDetails>
                        <Mui.List sx={{ width: "100%" }}>
                            {(Array.isArray(info.studentGroups) && info.studentGroups.length > 0
                                ? info.studentGroups
                                : [{ group: "-", effDt: "-" }]
                            ).map((code, idx) => (
                                <Mui.ListItem key={idx} sx={{ display: "flex", justifyContent: "space-between", px: 0 }} tabindex="0">
                                    <Mui.Typography variant="body2" sx={{ fontWeight: "bold" }}>
                                        Group: {code.group}
                                    </Mui.Typography>
                                    <Mui.Typography variant="body2" sx={{ fontWeight: "bold" }}>
                                        Effective Date: {code.effDt}
                                    </Mui.Typography>
                                </Mui.ListItem>
                            ))}
                        </Mui.List>
                    </Mui.AccordionDetails>
                    </Mui.Accordion>
                   
                </Mui.List>
            </Mui.Grid>

            <Mui.Grid item xs={4}>
                <Mui.List>
                    <DemographicItem label="CACR Score" value={info.cacrScore} />
                    <DemographicItem label="Load Date" value={info.applicationDate} />
                    <DemographicItem label="Recruitment Category" value={info.recruitmentCategory} />
                    
                    {/* Review Type Details Accordion */}
                    <Mui.Accordion sx={accordionStyle} expanded={expanded.reviewTypeDetails} onChange={handleExpand("reviewTypeDetails")}>
                    <Mui.AccordionSummary expandIcon={<MuiIcons.ExpandMore />} sx={summaryStyle}>
                        <Mui.Typography sx={{ fontWeight: "bold", fontSize: "0.85rem" }}>Review Type Details</Mui.Typography>
                    </Mui.AccordionSummary>
                    <Mui.AccordionDetails>
                        <Mui.List sx={{ width: "100%" }}>
                            <DemographicItem label="Type" value={humanizeEnum(info.reviewType, REVIEW_TYPE_LABELS)} />
                            <DemographicItem label="Reason" value={info.reviewTypeDecider} />
                        </Mui.List>
                    </Mui.AccordionDetails>
                </Mui.Accordion>

                    {/* Eminence Details Accordion */}
                    <Mui.Accordion sx={accordionSpecialStyle} expanded={expanded.eminenceDetails} onChange={handleExpand("eminenceDetails")}>
                    <Mui.AccordionSummary expandIcon={<MuiIcons.ExpandMore />} sx={summaryStyle}>
                        <Mui.Typography sx={{ fontWeight: "bold", fontSize: "0.85rem" }}>Eminence Details</Mui.Typography>
                    </Mui.AccordionSummary>
                    <Mui.AccordionDetails>
                        <Mui.List sx={{ width: "100%" }}>
                            <DemographicItem label="Applicant" value={info.isEminence} />
                            <DemographicItem label="Status" value={info.eminence} />
                        </Mui.List>
                    </Mui.AccordionDetails>
                </Mui.Accordion>

                    {/* Student Request Accordion */}
                    <Mui.Accordion sx={accordionSpecialStyle} expanded={expanded.studentRequest} onChange={handleExpand("studentRequest")}>
                    <Mui.AccordionSummary expandIcon={<MuiIcons.ExpandMore />} sx={summaryStyle}>
                        <Mui.Typography sx={{ fontWeight: "bold", fontSize: "0.85rem" }}>Student Request Details</Mui.Typography>
                    </Mui.AccordionSummary>
                    <Mui.AccordionDetails>
                        <Mui.List sx={{ width: "100%" }}>
                            <DemographicItem label="Request Active" value={info.studentRequestIsActive} />
                            <DemographicItem label="Request Type" value={humanizeEnum(info.studentRequestType, STUDENT_REQUEST_TYPE_LABELS)} />
                            <DemographicItem label="Case Number" value={info.studentRequestCaseNumber} />
                            <DemographicItem label="Request Date" value={info.studentRequestDate} />
                            <DemographicItem label="Reason" value={info.studentRequestReason} />
                            <DemographicItem label="Request Approved" value={info.studentRequestApproved} />
                            <DemographicItem label="Request Completed Date" value={info.studentRequestUpdatedDate} />
                        </Mui.List>
                    </Mui.AccordionDetails>
                </Mui.Accordion>
                
                    {/* Presidents Scholarship  Accordion */}
                    <Mui.Accordion sx={accordionStyle} expanded={expanded.presidentsScholarship} onChange={handleExpand("presidentsScholarship")}>
                    <Mui.AccordionSummary expandIcon={<MuiIcons.ExpandMore />} sx={summaryStyle}>
                        <Mui.Typography sx={{ fontWeight: "bold", fontSize: "0.85rem" }}>Presidents Scholarship Details</Mui.Typography>
                    </Mui.AccordionSummary>
                    <Mui.AccordionDetails>
                        <Mui.List sx={{ width: "100%" }}>
                            <DemographicItem label="Eligible" value={info.isPresidentsScholarshipProgram} />
                            <DemographicItem label="Date" value={info.presidentsScholarshipProgramDate} />
    
                        </Mui.List>
                    </Mui.AccordionDetails>
                </Mui.Accordion>

                </Mui.List>
            </Mui.Grid>

            <Mui.Grid item xs={4}>
                <Mui.List>
                    <DemographicItem label="Orientation Date" value={info.orientationDate} />
                    <DemographicItem label="Is Acceptance Fee Paid" value={info.isAcceptanceFeePaid} />
                    <DemographicItem label="Acceptance Fee Paid Date" value={info.acceptanceFeePaidDate} />
                    <DemographicItem label="Student Decision" value={info.studentDecision} />

                    {/* Decision Communications Accordion */}
                    <Mui.Accordion sx={accordionStyle} expanded={expanded.decisionComms} onChange={handleExpand("decisionComms")}>
                    <Mui.AccordionSummary expandIcon={<MuiIcons.ExpandMore />} sx={summaryStyle}>
                        <Mui.Typography sx={{ fontWeight: "bold", fontSize: "0.85rem" }}>Decision Communications</Mui.Typography>
                    </Mui.AccordionSummary>
                    <Mui.AccordionDetails>
                        <div style={{ overflowX: "auto", width: "100%" }}>
                        <Mui.Table size="small" sx={{  minWidth: 500, marginLeft: -2 }}>
                            <Mui.TableHead>
                                <Mui.TableRow>
                                    <Mui.TableCell>Context</Mui.TableCell>
                                    <Mui.TableCell>Code</Mui.TableCell>
                                    <Mui.TableCell>Description</Mui.TableCell>
                                    <Mui.TableCell>Date</Mui.TableCell> 
                                </Mui.TableRow>
                            </Mui.TableHead>
                            <Mui.TableBody>
                                {(Array.isArray(info.decisionCommunications) && info.decisionCommunications.length > 0
                                    ? info.decisionCommunications
                                    : [{ category: "-", letterCd: "-", letterCdDescription: "-", completedDt: "-", context: "-" }]
                                ).map((comm, idx) => (
                                    <Mui.TableRow key={idx}>
                                        <Mui.TableCell>{comm.context}</Mui.TableCell>
                                        <Mui.TableCell>{comm.letterCd}</Mui.TableCell>
                                        <Mui.TableCell>{comm.letterCdDescription}</Mui.TableCell>
                                        <Mui.TableCell>{comm.completedDt}</Mui.TableCell>
                                      
                                    </Mui.TableRow>
                                ))}
                            </Mui.TableBody>
                        </Mui.Table>
                        </div>
                    </Mui.AccordionDetails>
                </Mui.Accordion>
                
                    {/* Merit Scholarship Accordion */}
                    <Mui.Accordion sx={accordionSpecialStyle} expanded={expanded.meritScholarship} onChange={handleExpand("meritScholarship")}>
                        <Mui.AccordionSummary expandIcon={<MuiIcons.ExpandMore />} sx={summaryStyle}>
                            <Mui.Typography sx={{ fontWeight: "bold", fontSize: "0.85rem" }}>Merit Scholarship</Mui.Typography>
                        </Mui.AccordionSummary>
                        <Mui.AccordionDetails>
                            <div style={{ overflowX: "auto", width: "100%" }}>
                            <Mui.Table size="small" sx={{  minWidth: 500, marginLeft: -2 }}>
                                <Mui.TableHead>
                                    <Mui.TableRow>
                                        <Mui.TableCell>Context</Mui.TableCell>
                                        <Mui.TableCell>Code</Mui.TableCell>
                                        <Mui.TableCell>Description</Mui.TableCell>
                                        <Mui.TableCell>Date</Mui.TableCell>
                                        
                                    </Mui.TableRow>
                                </Mui.TableHead>
                                <Mui.TableBody>
                                    {(Array.isArray(info.meritScholarship) && info.meritScholarship.length > 0
                                        ? [...info.meritScholarship].sort((a, b) => (b.seq3c ?? 0) - (a.seq3c ?? 0))
                                        : [{ category: "-", letterCd: "-", letterCdDescription: "-", completedDt: "-", context: "-" }]
                                    ).map((merit, idx) => (
                                        <Mui.TableRow key={idx}>
                                            <Mui.TableCell>{merit.context}</Mui.TableCell>    
                                            <Mui.TableCell>{merit.letterCd}</Mui.TableCell>
                                            <Mui.TableCell>{merit.letterCdDescription}</Mui.TableCell>
                                            <Mui.TableCell>{merit.completedDt}</Mui.TableCell>
                                                 
                                        </Mui.TableRow>
                                    ))}
                                </Mui.TableBody>
                            </Mui.Table>
                            </div>
                        </Mui.AccordionDetails>
                    </Mui.Accordion>
                
                     {/* Service Indicator Accordion */}
                     <Mui.Accordion sx={accordionStyle} expanded={expanded.serviceIndicators} onChange={handleExpand("serviceIndicators")}>
                        <Mui.AccordionSummary expandIcon={<MuiIcons.ExpandMore />} sx={summaryStyle}>
                            <Mui.Typography sx={{ fontWeight: "bold", fontSize: "0.85rem" }}>Service Indicators</Mui.Typography>
                        </Mui.AccordionSummary>
                        <Mui.AccordionDetails>
                            <div style={{ overflowX: "auto", width: "100%" }}>
                            <Mui.Table size="small" sx={{  minWidth: 500, marginLeft: -2 }}>
                                <Mui.TableHead>
                                    <Mui.TableRow>
                                        <Mui.TableCell>Activation Date</Mui.TableCell>
                                        <Mui.TableCell>Activation Term</Mui.TableCell>
                                        <Mui.TableCell>Code</Mui.TableCell>
                                        <Mui.TableCell>Reason</Mui.TableCell>
                                    </Mui.TableRow>
                                </Mui.TableHead>
                                <Mui.TableBody>
                                    {(Array.isArray(info.serviceIndicators) && info.serviceIndicators.length > 0
                                        ? info.serviceIndicators
                                        : [{ activationDt: "-", activationTerm: "-", code: "-", reason: "-" }]
                                    ).map((code, idx) => (
                                        <Mui.TableRow key={idx}>
                                            <Mui.TableCell>{code.activationDt}</Mui.TableCell>
                                            <Mui.TableCell>{code.activationTerm}</Mui.TableCell>
                                            <Mui.TableCell>{code.code}</Mui.TableCell>
                                            <Mui.TableCell>{code.reason}</Mui.TableCell>
                                        </Mui.TableRow>
                                    ))}
                                </Mui.TableBody>
                            </Mui.Table>
                            </div>
                        </Mui.AccordionDetails>
                    </Mui.Accordion>

                          {/* Publish Details Accordion */}
                    <Mui.Accordion sx={accordionSpecialStyle} expanded={expanded.publishDetails} onChange={handleExpand("publishDetails")}>
                    <Mui.AccordionSummary expandIcon={<MuiIcons.ExpandMore />} sx={summaryStyle}>
                        <Mui.Typography sx={{ fontWeight: "bold", fontSize: "0.85rem" }}>Publish Details</Mui.Typography>
                    </Mui.AccordionSummary>
                    <Mui.AccordionDetails>
                        <Mui.List sx={{ width: "100%" }}>
                            <DemographicItem label="Decision" value={humanizeEnum(info.decision, DECISION_LABELS)} />
                            <DemographicItem label="Date" value={info.publishDateTime} />
                        </Mui.List>
                    </Mui.AccordionDetails>
                </Mui.Accordion>
             
                </Mui.List>
            </Mui.Grid>
        </Mui.Grid>
    );
}
